/**
 * @file AnnotationGroupDialog.jsx
 * @description wraps the annotationGroupContainer
 *
 * @copyright veriome labs, llc.  - all rights reserved.
 * This file is subject to the terms and conditions defined in
 * file 'LICENSE.md', which is part of this source code package.
 *
 * use of draggable:
 * https://github.com/adnane1975/dragdialog/blob/main/src/app/dialog.js
 * 
 * @author don michael
 * @since Sep 12, 2023 21:24
 */
import { forwardRef, useEffect, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';

import AddIcon from '@mui/icons-material/Add';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Divider from '@mui/material/Divider';
import Fab from '@mui/material/Fab';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import IconButton from '@mui/material/IconButton';
import { makeStyles } from '@mui/styles';
import Slide from '@mui/material/Slide';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';

import Draggable from 'react-draggable';

import AnnotationGroupContainer from './AnnotationGroupContainer';
import annotationSavePrep from 'src/store/workers/annotationsSavePrep';

import { arrayExists, objectExists, stringExists } from 'src/@common/utilities';
import { createId } from 'src/@common/dbUtilities';

import { pubSub, pubSubEvents } from 'src/services/PubSub';

import standardMessages from 'src/services/notifications/standardMessages';

import {
    enqueueMessage,
} from 'src/store/actions/global.actions';

import {
    annotationEditOpenDialogSet,
    annotationNewRequest,
    annotationsSaveRequest,
} from 'src/store/actions/documentAnnotator/documentAnnotatorDb.actions';

const useStyles = makeStyles(() => ({
    dialogPaper: {
        minHeight: '15vh',
        maxHeight: '25vh',
    },
}));

function PaperComponent(props) {
    const nodeRef = useRef(null);
    return (
        <Draggable nodeRef={nodeRef} cancel={'[class*="MuiDialogContent-root"]'}>
            <Paper ref={nodeRef} {...props} />
        </Draggable>
    );
}

const Transition = forwardRef(function Transition(props, ref) {
    return <Slide direction="left" ref={ref} {...props} />;
});

export default function AnnotationGroupDialog(props) {
    const {
        title = 'Annotation Group',
        canAddAnnotation = true,
        canDeleteAnnotation = true,
    } = props;

    const classes = useStyles();
    const dispatch = useDispatch();

    // start: redux state --------------------------
    const documentAnnotatorPagesState = useSelector((state) => {
        return state.documentAnnotatorPages;
    });

    const {
        docType,
        docId,
        pmId,
        pubId
    } = documentAnnotatorPagesState;

    const documentAnnotatorDbState = useSelector((state) => {
        return state.documentAnnotatorDb;
    });

    const {
        annotationEditOpenDialog,
        annotationGroup,
    } = documentAnnotatorDbState;

    // end: redux state -----------------------------

    const [annotationGroupId, setAnnotationGroupId] = useState('');
    // const [saveIsDisabled, setSaveIsDisabled] = useState(true);

    useEffect(() => {
        if (objectExists(annotationGroup)) {

            if (stringExists(annotationGroup.annotationGroupId)) {
                setAnnotationGroupId(annotationGroup.annotationGroupId);
            }

            // const revisedAnnotations = annotationSavePrep(annotationGroup);

            // if (arrayExists(revisedAnnotations)) {
            //     setSaveIsDisabled(false);
            // } else {
            //     setSaveIsDisabled(true);
            // }
        }
    }, [annotationGroup]);

    const handleClose = () => {
        // save, if dirty
        handleSave();

        dispatch(annotationEditOpenDialogSet(false));
    };

    const handleSave = () => {
        const revisedAnnotations = annotationSavePrep(annotationGroup);

        if (arrayExists(revisedAnnotations)) {

            dispatch(annotationsSaveRequest(revisedAnnotations, true));

            const annotationIds = [];
            revisedAnnotations.forEach((annotation) => {
                annotationIds.push(annotation._id);
            });

            setTimeout(() => {
                pubSub.publish(pubSubEvents.AnnotationsSaved, annotationIds);
            }, 1000);

        } else {
            // send a reassuring 'saved' message anyway
            dispatch(enqueueMessage(
                standardMessages.successfulSave.message,
                standardMessages.successfulSave.options)
            );
        }

    }

    const handleNewAnnotation = () => {

        const newAnnotation = {
            _id: createId(),
            annotationType: '',
            annotationGroupId,
            pmId,
            pubId,
            parentDocType: docType,
            parentId: docId,
            curatedText: annotationGroup.curatedText,
            text: annotationGroup.curatedText,
        };

        dispatch(annotationNewRequest(newAnnotation));
    }

    return (

        <Dialog
            hideBackdrop
            open={annotationEditOpenDialog}
            onClose={handleClose}
            PaperComponent={PaperComponent}
            classes={{ paper: classes.dialogPaper }}
            aria-labelledby="dialog-title"
            scroll={'paper'}
            fullWidth maxWidth="sm"
            TransitionComponent={Transition}
            closeAfterTransition={false}
        >
            <DialogTitle style={{ cursor: 'move', paddingBottom: '0px', marginBottom: '10px' }} id="dialog-title">
                <Typography style={{ color: 'white', backgroundColor: '#5569ff', paddingTop: '12px', paddingBottom: '12px', paddingLeft: '15px' }}
                    variant="h4" component="div"
                    sx={{ flexGrow: 1 }}
                >
                    {title}
                </Typography>
                <Divider />
            </DialogTitle>

            <Button
                variant="filled"
                size="small"
                onClick={handleClose}
                sx={{
                    position: 'absolute',
                    right: 25,
                    top: 20,
                    marginRight: '5px',
                    color: (theme) => theme.colors.alpha.trueWhite[100],
                }}
            >
                Close
            </Button>

            <DialogContent dividers>

                <AnnotationGroupContainer
                    canDeleteAnnotation={canDeleteAnnotation}
                />

            </DialogContent>

            <DialogActions>

                {canAddAnnotation &&
                    <Grid
                        container
                        direction="row"
                        justifyContent="space-between"
                        alignItems="flex-start"
                    >

                        <IconButton aria-label="add annotation" size="large">

                            <Tooltip title="Add an annotation to the group" placement="top">

                                <Fab
                                    color='success'
                                    component='span'
                                    size="small"
                                    aria-label="add"
                                    sx={{
                                        mt: '8px',
                                        mr: '8px',
                                    }}
                                >
                                    <AddIcon
                                        onClick={(ev) => {
                                            ev.preventDefault();
                                            ev.stopPropagation();
                                            handleNewAnnotation();
                                        }}
                                    />
                                </Fab>

                            </Tooltip>
                        </IconButton>

                    </Grid>
                }

                <Grid
                    container
                    direction="row"
                    justifyContent="flex-end"
                    alignItems="flex-end"
                >
                    <Tooltip title={'Save changes'}>
                        <Button
                            sx={{
                                mt: '8px',
                                mr: '8px',
                            }}
                            variant="contained"
                            color="primary"
                            // disabled={saveIsDisabled}
                            onClick={(ev) => {
                                ev.preventDefault();
                                ev.stopPropagation();
                                handleSave();
                            }}
                        >
                            Save
                        </Button>
                    </Tooltip>

                </Grid>

            </DialogActions>

        </Dialog >

    );
}

AnnotationGroupDialog.propTypes = {
    title: PropTypes.string,
    canAddAnnotation: PropTypes.bool,
    canDeleteAnnotation: PropTypes.bool,
};